import { useState } from 'react';
import {
    Button,
    Box,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Divider,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AirlineStopsIcon from '@mui/icons-material/AirlineStops';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import BusinessIcon from '@mui/icons-material/Business';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import EmailIcon from '@mui/icons-material/Email';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../contexts/AuthContext';
import { Environment } from '../vite-env';

interface NavBarProps {
    currentPath: string;
    onLogout?: () => void;
}

const navItems = [
    {
        path: '/', //
        label: 'Overview',
        icon: <HomeIcon />,
        roles: ['admin', 'developer', 'finance', 'user'],
    },
    {
        path: '/events',
        label: 'Events',
        icon: <AirlineStopsIcon />,
        roles: ['admin', 'developer', 'finance', 'user'],
    },
    {
        path: '/api-calls',
        label: 'API Calls',
        icon: <ImportExportIcon sx={{ transform: 'scaleX(-1)' }} />,
        roles: ['admin', 'developer', 'finance', 'user'],
    },
    {
        path: '/invoices',
        label: 'Invoices',
        icon: <ReceiptIcon />,
        roles: ['admin', 'finance', 'teacher'],
    },
    {
        path: '/payments',
        label: 'Payments',
        icon: <PaymentIcon />,
        roles: ['admin', 'finance', 'teacher'],
    },
    {
        path: '/products',
        label: 'Products',
        icon: <ShoppingCartIcon />,
        roles: ['admin', 'developer', 'finance', 'user'],
    },
    {
        path: '/centres',
        label: 'Centres',
        icon: <BusinessIcon />,
        roles: ['admin', 'developer', 'finance', 'user'],
    },
    {
        path: '/icontact',
        label: 'iContact',
        icon: <EmailIcon />,
        roles: ['admin', 'developer', 'finance', 'teacher', 'user'],
    },
    {
        path: '/users', //
        label: 'Users',
        icon: <PeopleIcon />,
        roles: ['admin'],
    },
];

export function NavBar({ currentPath, onLogout }: NavBarProps) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { role } = useAuth();

    const environment = import.meta.env.VITE_ENVIRONMENT as Environment;
    const isDev = environment === 'dev';

    // Filter navigation items based on user role
    const filteredNavItems = navItems.filter((item) => {
        if (isDev) {
            return true; // Show all items in dev environment
        } else {
            return item.roles.includes(role || 'undefined');
        }
    });

    const navigate = (path: string) => {
        window.history.pushState({}, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));
        setDrawerOpen(false);
    };

    const handleLogout = () => {
        setDrawerOpen(false);
        onLogout?.();
    };

    // Mobile: Hamburger menu
    if (isMobile) {
        return (
            <>
                <IconButton
                    color="inherit"
                    aria-label="open navigation menu"
                    onClick={() => setDrawerOpen(true)}
                    sx={{ color: 'text.secondary' }}
                >
                    <MenuIcon />
                </IconButton>
                <Drawer
                    anchor="left"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    PaperProps={{
                        sx: { width: 250, backgroundColor: 'background.paper' },
                    }}
                >
                    <Box sx={{ pt: 2 }}>
                        <List>
                            {filteredNavItems.map((item) => (
                                <ListItem key={item.path} disablePadding>
                                    <ListItemButton
                                        onClick={() => navigate(item.path)}
                                        selected={currentPath === item.path}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                color: currentPath === item.path ? 'primary.main' : 'text.secondary',
                                            }}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.label}
                                            sx={{ color: currentPath === item.path ? 'primary.main' : 'text.primary' }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                        {onLogout && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <List>
                                    <ListItem disablePadding>
                                        <ListItemButton onClick={handleLogout}>
                                            <ListItemIcon sx={{ color: 'text.secondary' }}>
                                                <LogoutIcon />
                                            </ListItemIcon>
                                            <ListItemText primary="Logout" />
                                        </ListItemButton>
                                    </ListItem>
                                </List>
                            </>
                        )}
                    </Box>
                </Drawer>
            </>
        );
    }

    // Desktop: Regular navigation buttons
    return (
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            {filteredNavItems.map((item) => (
                <Button
                    key={item.path}
                    color="inherit"
                    startIcon={item.icon}
                    onClick={() => navigate(item.path)}
                    sx={{
                        color: currentPath === item.path ? 'primary.main' : 'text.secondary',
                        fontWeight: currentPath === item.path ? 600 : 400,
                    }}
                >
                    {item.label}
                </Button>
            ))}
        </Box>
    );
}
